import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFolder,
  faInfoCircle,
  faPlusCircle,
} from '@fortawesome/free-solid-svg-icons'
import { PROJECTS_ROUTE } from 'src/constants/routes'
import { useDeleteProjectMutation } from 'src/reactQuery/useDeleteProjectMutation'
import { useGetProjectsQuery } from 'src/reactQuery/useGetProjectsQuery'
import { ProjectModules } from 'src/services/projectsApi/interfaces/projects.interface'
import { ProjectActions } from 'src/pages/ProjectsPage/ProjectActions'
import { Skeleton } from 'src/components/Skeleton'
import { formatIndustries, getProjectAppearance } from './projectAppearance'
import styles from './projectsWidget.module.scss'
import { CreateProjectModal } from './CreateProjectModal'

// From projectsWidget.module.scss: .projectCard is 88px min-height / radius 18,
// and .projectsList is max-height 388px with a 12px gap — so three cards fill
// the list without scrolling (3 * 88 + 2 * 12 = 288).
const PROJECT_CARD_HEIGHT = 88
const PROJECT_CARD_RADIUS = 18
const PROJECT_SKELETON_COUNT = 3

interface ProjectsWidgetProps {
  variant?: 'widget' | 'page'
}

export const ProjectsWidget: FC<ProjectsWidgetProps> = ({
  variant = 'widget',
}) => {
  const navigate = useNavigate()
  const { data, isLoading } = useGetProjectsQuery()
  const projects = data?.data || []

  // Without this the widget claimed "No projects created yet" while the query
  // was still in flight — projects is [] before the response arrives, which is
  // indistinguishable from genuinely having none. Loading and empty are
  // different states and must render differently.
  const isInitialLoading = isLoading && !data
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] =
    useState<boolean>(false)
  const [projectToEdit, setProjectToEdit] = useState<{
    id: string
    industries: string[]
    modules: ProjectModules
    name: string
  } | null>(null)
  const deleteProjectMutation = useDeleteProjectMutation({
    onSuccess: () => {
      setProjectToEdit(null)
    },
  })

  const formatDateTime = (value: string) => {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'

    return date.toLocaleString('en-US', {
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      month: 'short',
    })
  }

  return (
    <div
      className={
        variant === 'widget' ? styles.projectsWidgetContainer : undefined
      }
    >
      <div className={styles.projectsHeader}>
        <div className={styles.projectsHeaderLeft}>
          <h3 className={styles.projectsTitle}>Projects</h3>

          <div className={styles.projectsSubtitleRow}>
            <FontAwesomeIcon className={styles.infoIcon} icon={faInfoCircle} />
            <span className={styles.projectsSubtitle}>
              Select a project to view campaign analysis and insights
            </span>
          </div>
        </div>

        <button
          className={styles.createProjectButton}
          onClick={() => setIsCreateProjectModalOpen(true)}
          type="button"
        >
          <FontAwesomeIcon
            className={styles.createProjectButtonIcon}
            icon={faPlusCircle}
          />
          <span>Create project</span>
        </button>
      </div>

      {variant === 'widget' && <div className={styles.projectsDivider} />}

      {isInitialLoading ? (
        <div className={styles.projectsList}>
          {Array.from({ length: PROJECT_SKELETON_COUNT }).map((_, index) => (
            <Skeleton
              height={PROJECT_CARD_HEIGHT}
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              radius={PROJECT_CARD_RADIUS}
            />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className={styles.emptyStateRow}>
          <div className={styles.emptyStateIconContainer}>
            <FontAwesomeIcon
              className={styles.emptyStateIcon}
              icon={faFolder}
            />
          </div>

          <div className={styles.emptyStateTextContent}>
            <h4 className={styles.emptyStateTitle}>No projects created yet</h4>
            <p className={styles.emptyStateSubtitle}>
              Create your first project to start tracking campaigns, sentiment,
              analytics, and insights.
            </p>
          </div>
        </div>
      ) : (
        <div className={styles.projectsList}>
          {projects.map((project) => {
            const { Icon, accent, background } = getProjectAppearance(
              project.industries
            )

            return (
              <article className={styles.projectCard} key={project._id}>
                <div className={styles.projectCardLeft}>
                  <span
                    className={styles.projectIconBox}
                    style={{ background, color: accent }}
                  >
                    <Icon className={styles.projectIcon} />
                  </span>

                  <div className={styles.projectText}>
                    <h3 className={styles.projectName}>{project.name}</h3>
                    <p className={styles.projectIndustries}>
                      {formatIndustries(project.industries)}
                    </p>
                  </div>
                </div>

                <div className={styles.projectCardRight}>
                  <div className={styles.projectDates}>
                    <span>Added at {formatDateTime(project.createdAt)}</span>
                    <span>Updated at {formatDateTime(project.updatedAt)}</span>
                  </div>

                  <span className={styles.activeBadge}>Active</span>

                  <button
                    className={styles.projectDetailsButton}
                    onClick={() => navigate(`${PROJECTS_ROUTE}/${project._id}`)}
                    type="button"
                  >
                    View Project Details
                  </button>

                  <ProjectActions
                    onDelete={() => {
                      deleteProjectMutation.mutate(project._id)
                    }}
                    onEdit={() => {
                      setProjectToEdit({
                        id: project._id,
                        industries: project.industries,
                        modules: project.modules,
                        name: project.name,
                      })
                    }}
                  />
                </div>
              </article>
            )
          })}
        </div>
      )}

      <CreateProjectModal
        isOpen={isCreateProjectModalOpen}
        onClose={() => setIsCreateProjectModalOpen(false)}
        onSuccess={() => navigate(PROJECTS_ROUTE)}
      />

      {projectToEdit && (
        <CreateProjectModal
          initialIndustryIds={projectToEdit.industries}
          initialModules={projectToEdit.modules}
          initialProjectName={projectToEdit.name}
          isEditMode
          isOpen={!!projectToEdit}
          onClose={() => setProjectToEdit(null)}
          onDelete={() => {
            deleteProjectMutation.mutate(projectToEdit.id)
          }}
          projectId={projectToEdit.id}
        />
      )}
    </div>
  )
}
